import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import * as React from "react";
import { useCookies } from "react-cookie";
import axiosInstance from "../../axiosInstance";

interface ApiKey {
  key_prefix: string;
  label: string;
  sites: string[] | null;
  created_at: number;
  last_used_at: number;
}

interface SiteInfo {
  siteId: string;
  siteName: string;
}

const ConfigIntegrations = () => {
  const [cookies] = useCookies(["rp_user_data"]);

  // --- MCP Connected Apps state ---
  const [mcpApps, setMcpApps] = React.useState<any[]>([]);
  const [mcpRevokeRes, setMcpRevokeRes] = React.useState({
    message: "",
    isVisible: false,
    isError: false,
  });

  // --- Permanent API Keys state ---
  const [apiKeys, setApiKeys] = React.useState<ApiKey[]>([]);
  const [newApiKey, setNewApiKey] = React.useState<string>("");
  const [apiKeyLabel, setApiKeyLabel] = React.useState<string>("");
  const [apiKeyLoading, setApiKeyLoading] = React.useState(false);
  const [apiKeyCopied, setApiKeyCopied] = React.useState(false);
  const [apiKeyRes, setApiKeyRes] = React.useState({
    message: "",
    isVisible: false,
    isError: false,
  });

  // --- Site scoping state ---
  const [siteMode, setSiteMode] = React.useState<"all" | "specific">("all");
  const [userSites, setUserSites] = React.useState<SiteInfo[]>([]);
  const [selectedSiteIds, setSelectedSiteIds] = React.useState<Set<string>>(new Set());
  const [showCreateForm, setShowCreateForm] = React.useState(false);

  async function copyToClipboard(
    text: string,
    setCopied: React.Dispatch<React.SetStateAction<boolean>>
  ) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function fetchMcpApps() {
    try {
      const response = await axiosInstance.get("/api/mcp-connected-apps");
      setMcpApps(response.data.apps || []);
    } catch (err) {
      console.log("fetchMcpApps error", err);
    }
  }

  async function revokeMcpApp(clientId: string) {
    try {
      const response = await axiosInstance.post("/api/revoke-mcp-app", {
        clientId,
      });
      setMcpRevokeRes({
        message: response.data.message,
        isVisible: true,
        isError: false,
      });
      fetchMcpApps();
    } catch (err: any) {
      setMcpRevokeRes({
        message: err.response?.data?.message || "Failed to revoke",
        isVisible: true,
        isError: true,
      });
    } finally {
      setTimeout(() => {
        setMcpRevokeRes((old) => ({ ...old, isVisible: false }));
      }, 5000);
    }
  }

  // --- Permanent API Key functions ---
  async function fetchApiKeys() {
    try {
      const response = await axiosInstance.get("/oauth/api-keys");
      setApiKeys(response.data.api_keys || []);
    } catch (err) {
      console.log("fetchApiKeys error", err);
    }
  }

  async function fetchUserSites() {
    try {
      const response = await axiosInstance.get("/oauth/api-key-permissions");
      const sites = (response.data.sites || []).map((s: any) => ({
        siteId: s.siteId,
        siteName: s.siteName,
      }));
      setUserSites(sites);
    } catch (err) {
      console.log("fetchUserSites error", err);
    }
  }

  function resetForm() {
    setApiKeyLabel("");
    setSiteMode("all");
    setSelectedSiteIds(new Set());
  }

  function openCreateForm() {
    resetForm();
    setShowCreateForm(true);
    fetchUserSites();
  }

  function toggleSiteSelection(siteId: string) {
    setSelectedSiteIds((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  }

  async function createApiKey() {
    if (siteMode === "specific" && selectedSiteIds.size === 0) {
      setApiKeyRes({
        message: "Please select at least one site.",
        isVisible: true,
        isError: true,
      });
      setTimeout(() => setApiKeyRes((old) => ({ ...old, isVisible: false })), 5000);
      return;
    }

    setApiKeyLoading(true);
    try {
      const sites = siteMode === "all" ? ["*"] : Array.from(selectedSiteIds);

      const response = await axiosInstance.post("/oauth/api-keys", {
        label: apiKeyLabel.trim() || undefined,
        sites,
      });
      setNewApiKey(response.data.api_key || "");
      setShowCreateForm(false);
      resetForm();
      setApiKeyRes({
        message: "API key created. Copy it now \u2014 it won't be shown again.",
        isVisible: true,
        isError: false,
      });
      fetchApiKeys();
    } catch (err: any) {
      const msg =
        err.response?.data?.error_description ||
        err.response?.data?.message ||
        "Failed to create API key";
      setApiKeyRes({ message: msg, isVisible: true, isError: true });
    } finally {
      setApiKeyLoading(false);
      setTimeout(() => setApiKeyRes((old) => ({ ...old, isVisible: false })), 10000);
    }
  }

  async function revokeApiKey(keyPrefix: string) {
    try {
      await axiosInstance.delete(`/oauth/api-keys/${keyPrefix}`);
      setApiKeyRes({
        message: "API key revoked successfully.",
        isVisible: true,
        isError: false,
      });
      fetchApiKeys();
      if (newApiKey.startsWith(keyPrefix)) {
        setNewApiKey("");
      }
    } catch (err: any) {
      const msg =
        err.response?.data?.error_description ||
        err.response?.data?.message ||
        "Failed to revoke API key";
      setApiKeyRes({ message: msg, isVisible: true, isError: true });
    } finally {
      setTimeout(() => setApiKeyRes((old) => ({ ...old, isVisible: false })), 5000);
    }
  }

  React.useEffect(() => {
    fetchMcpApps();
    fetchApiKeys();
  }, []);

  return (
    <div className="flex flex-col text-black h-screen flex-1 md:mr-4 mt-2.5 md:mt-0">
      <h1 style={{ color: "#243060", fontSize: "24px", fontWeight: 700, marginTop: 0, marginBottom: "24px" }}>
        Connected Apps & API Keys
      </h1>

      {/* Permanent API Keys Section */}
      <div className="pt-5 border-t border-slate-300 mb-8">
        <label
          style={{
            color: "#243060",
            marginBottom: "12px",
            display: "flex",
          }}
        >
          Permanent API Keys
        </label>
        <p
          className="font-normal text-sm mb-4"
          style={{ color: "#3B3B3B" }}
        >
        Create API keys to access the RocketPages API from your apps, scripts, and integrations. Keys never expire and can be revoked whenever needed.
        </p>

        {/* New key display (shown once after creation) */}
        {newApiKey && (
          <div className="mb-3">
            <label className="text-sm font-semibold" style={{ color: "#3B3B3B" }}>
              New API Key
            </label>
            <div className="flex items-center gap-2 p-3 bg-gray-100 rounded border border-gray-300 mt-1">
              <code
                className="flex-1 text-sm break-all"
                style={{ color: "#243060" }}
              >
                {newApiKey}
              </code>
              <Button
                size="small"
                onClick={() => copyToClipboard(newApiKey, setApiKeyCopied)}
                sx={{
                  minWidth: "auto",
                  padding: "4px 8px",
                  color: "#243060",
                  textTransform: "none",
                }}
                startIcon={<ContentCopyRoundedIcon fontSize="small" />}
              >
                {apiKeyCopied ? "Copied!" : "Copy"}
              </Button>
            </div>
          </div>
        )}

        {apiKeyRes.isVisible && (
          <div className="mb-3">
            <span
              className={`text-sm ${
                apiKeyRes.isError ? "text-red-500" : "text-green-500"
              }`}
            >
              {apiKeyRes.message}
            </span>
          </div>
        )}

        {/* Create key button / form toggle */}
        {!showCreateForm ? (
          <div className="flex items-center gap-2 mb-4">
            <Button
              variant="contained"
              size="small"

              onClick={openCreateForm}
              sx={{
                backgroundColor: "#243060",
                border: "1px solid",
                borderColor: "#243060",
                color: "#fff",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1a2347",
                  color: "#fff",
                },
              }}
            >
              Create API Key
            </Button>
          </div>
        ) : (
          <div className="mb-4 p-4 border border-gray-300 rounded-lg bg-gray-50">
            {/* Key label */}
            <div className="mb-4">
              <label className="text-sm font-semibold block mb-1" style={{ color: "#3B3B3B" }}>
                Key Label
              </label>
              <TextField
                size="small"
                placeholder="e.g. My CI/CD key (optional)"
                value={apiKeyLabel}
                onChange={(e) => setApiKeyLabel(e.target.value.slice(0, 64))}
                inputProps={{ maxLength: 64 }}
                sx={{ width: "100%", maxWidth: 400, '& input::placeholder': { fontSize: '0.75rem' } }}
              />
            </div>

            {/* Site access selector */}
            <div className="mb-4">
              <label className="text-sm font-semibold block mb-1" style={{ color: "#3B3B3B" }}>
                Site Access
              </label>
              <RadioGroup
                value={siteMode}
                onChange={(e) => setSiteMode(e.target.value as "all" | "specific")}
              >
                <FormControlLabel
                  value="all"
                  control={<Radio size="small" sx={{ color: "#243060", "&.Mui-checked": { color: "#243060" } }} />}
                  label={<span className="text-sm">All sites</span>}
                />
                <FormControlLabel
                  value="specific"
                  control={<Radio size="small" sx={{ color: "#243060", "&.Mui-checked": { color: "#243060" } }} />}
                  label={<span className="text-sm">Specific sites</span>}
                />
              </RadioGroup>
              {siteMode === "specific" && (
                <div className="ml-7 mt-1 max-h-40 overflow-y-auto border border-gray-200 rounded p-2 bg-white">
                  {userSites.length === 0 ? (
                    <p className="text-xs" style={{ color: "#6B7280" }}>Loading sites...</p>
                  ) : (
                    userSites.map((site) => (
                      <FormControlLabel
                        key={site.siteId}
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedSiteIds.has(site.siteId)}
                            onChange={() => toggleSiteSelection(site.siteId)}
                            sx={{ color: "#243060", "&.Mui-checked": { color: "#243060" }, py: 0.25 }}
                          />
                        }
                        label={<span className="text-sm">{site.siteName}</span>}
                        sx={{ display: "flex", mb: 0 }}
                      />
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Form actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="contained"
                size="small"
                disabled={apiKeyLoading}
                onClick={createApiKey}
                sx={{
                  backgroundColor: "#243060",
                  border: "1px solid",
                  borderColor: "#243060",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#1a2347",
                    color: "#fff",
                  },
                }}
              >
                {apiKeyLoading ? "Creating..." : "Create Key"}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => { setShowCreateForm(false); resetForm(); }}
                sx={{
                  borderColor: "#243060",
                  color: "#243060",
                  textTransform: "none",
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <p className="text-xs mb-3" style={{ color: "#6B7280" }}>
          {apiKeys.length} {apiKeys.length === 1 ? "key" : "keys"} created
        </p>

        {/* Key list */}
        {apiKeys.length > 0 &&
          apiKeys.map((key) => (
            <div
              key={key.key_prefix}
              className="flex flex-col items-start justify-between sm:flex-row sm:items-center mb-4"
            >
              <div className="flex flex-col">
                <label style={{ fontWeight: 600, color: "#3B3B3B" }}>
                  {key.key_prefix}...
                  {key.label && (
                    <span style={{ fontWeight: 400, marginLeft: 8, color: "#6B7280" }}>
                      {key.label}
                    </span>
                  )}
                </label>
                <p
                  className="mb-0 font-normal text-xs mt-1"
                  style={{ color: "#6B7280" }}
                >
                  Sites: {key.sites ? (key.sites.includes("*") ? "All sites" : `${key.sites.length} site${key.sites.length !== 1 ? "s" : ""}`) : "All sites"}
                </p>
                <p
                  className="mb-0 font-normal text-xs mt-0.5"
                  style={{ color: "#6B7280" }}
                >
                  Created {new Date(key.created_at).toLocaleDateString()}{" "}
                  {key.last_used_at && (
                    <>
                      &middot; Last used{" "}
                      {new Date(key.last_used_at).toLocaleDateString()}
                    </>
                  )}
                </p>
              </div>
              <div className="flex ml-0 mt-2 sm:ml-0 sm:mt-0">
                <Button
                  className="px-4 py-2"
                  variant="contained"
                  size="small"
                  onClick={() => revokeApiKey(key.key_prefix)}
                  sx={{
                    backgroundColor: "#fff",
                    border: "1px solid",
                    borderColor: "#ff0000",
                    color: "#ff0000",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#ff0000",
                      color: "#fff",
                    },
                  }}
                >
                  Revoke
                </Button>
              </div>
            </div>
          ))}

        {apiKeys.length === 0 && (
          <p className="text-sm" style={{ color: "#6B7280" }}>
            No API keys created yet.
          </p>
        )}
      </div>

      {/* MCP Connected Apps Section */}
      <div className="pt-5 border-t border-slate-300 mcp-connected-apps">
        <label
          style={{
            color: "#243060",
            marginBottom: "12px",
            display: "flex",
          }}
        >
          Connected Apps
        </label>
        <p
          className="font-normal text-sm mb-4"
          style={{ color: "#3B3B3B" }}
        >
          {mcpApps.length === 0
            ? "No apps or tools are currently connected to your site."
            : "MCP tools and APIs currently connected to your site. You can revoke access at any time."}
        </p>

        {mcpRevokeRes.isVisible && (
          <div className="mb-3">
            <span
              className={`text-sm ${
                mcpRevokeRes.isError
                  ? "text-red-500"
                  : "text-green-500"
              }`}
            >
              {mcpRevokeRes.message}
            </span>
          </div>
        )}

        {mcpApps.length > 0 &&
          mcpApps.map((app) => (
            <div
              key={app.clientId}
              className="flex flex-col items-start justify-between sm:flex-row sm:items-center mb-4"
            >
              <div className="flex flex-col">
                <label style={{ fontWeight: 600, color: "#3B3B3B" }}>
                  {app.clientName}
                </label>
                <p
                  style={{ color: "#3B3B3B" }}
                  className="mb-0 font-normal text-sm"
                >
                  Grants full access to RocketPages APIs
                </p>
                <p
                  className="mb-0 font-normal text-sm mt-1"
                  style={{ color: "#6B7280" }}
                >
                  Connected on{" "}
                  {new Date(app.connectedAt).toLocaleDateString()}{" "}
                  at {new Date(app.connectedAt).toLocaleTimeString()}
                </p>
              </div>
              <div className="flex ml-0 mt-2 sm:ml-0 sm:mt-0">
                <Button
                  className="px-4 py-2"
                  variant="contained"
                  size="small"
                  onClick={() => revokeMcpApp(app.clientId)}
                  sx={{
                    backgroundColor: "#fff",
                    border: "1px solid",
                    borderColor: "#ff0000",
                    color: "#ff0000",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#ff0000",
                      color: "#fff",
                    },
                  }}
                >
                  Revoke
                </Button>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default ConfigIntegrations;
